import { Injectable, inject, DOCUMENT } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { LEGACY_ASP_SESSION_PROBE } from '@neworg-net/amsuite-core/auth';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { requireHostUrl } from '../shared/required-host-url';

import { Observable, of, lastValueFrom } from 'rxjs';
import { Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { httpOptions } from "../constants/http-options.constant";

import { CookieOptions, CookieService } from 'ngx-cookie';

import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: "root"
})

export class AppService {
  private _document = inject(DOCUMENT);
  private _http = inject(HttpClient);
  private _cookieService = inject(CookieService);
  private readonly config = inject(ConfigService);


  private suiteUrl = this.getServerBaseUrl + '/suite';
  public serviceUrl = this.getServerBaseUrl + '/auth';
  public partyUrl = this.getServerBaseUrl + '/party';
  private serviceGeographyUrl = this.getServerBaseUrl + '/geography';
  private activityUrl = this.getServerBaseUrl + '/activity';
  private notificationUrl = this.getServerBaseUrl + '/notification';


  public locale: string;
  public remove$ = new Subject();
  private headers = new HttpHeaders();

  public get getServerBaseUrl(): string {
    return this.getServerADBaseUrl;
  }

  get getServerADBaseUrl(): string {
    if (this._document.location.host.includes('demo.')) {
      return requireHostUrl(this.config, 'demo');
    }
    return requireHostUrl(this.config, 'gatewayAD');
  }

  /**
   * Host dell'API RefundedCosts V3, DISTINTO dal gateway del monolite. Lo consuma il widget
   * note spese (`[baseUrlV3]` di `<employee-refunded-costs>`): con questo Input la libreria
   * passa all'aggregato `/context` — UNA chiamata al posto delle quattro al monolite per
   * valute, tipologie, metodi di pagamento e settings — e alle rotte V3 per liste e voci.
   * Senza, resta interamente sul monolite.
   *
   * ⚠️ `hostUrl` e NON `requireHostUrl`, deliberatamente: con la chiave assente dal registry
   * il getter torna `null`, la libreria non vede l'Input e degrada al percorso monolite. Un
   * throw qui renderebbe inservibile l'INTERA dashboard per una chiave mancante, che e' una
   * degradazione sproporzionata rispetto al perdere il ramo V3 di un widget. Stessa scelta,
   * per la stessa ragione, di `getRefundedCostsApiBaseUrl` nell'app refunded-costs.
   */
  public get getRefundedCostsApiBaseUrl(): string | null {
    return this.config.hostUrl('refundedcosts') ?? null;
  }



  //#region User Datas

  public getUser(): Observable<any> {
    const url = `${this.serviceUrl}/user`;
    return this._http.get(url, httpOptions);
  }

  public getRealUser(): Observable<any> {
    const url = `${this.serviceUrl}/realuser`;
    return this._http.get(url, httpOptions);
  }

  getUserCompanies(): Observable<any> {
    return this._http.get(this.suiteUrl, httpOptions);
  }

  /** Stesso lookup usato dalle app satellite: il suite owner non coincide necessariamente con l'azienda. */
  getPartySuiteOwner(idParty: string): Observable<string> {
    return this._http.get<string>(`${this.partyUrl}/getpartysuiteowner`, {
      ...httpOptions,
      params: new HttpParams().set('idParty', idParty),
    });
  }

  async getUserPermissions(): Promise<any> {
    try {
      const url = `${this.serviceUrl}/userpermissions?appId=${this.config.appId}`;
      return await lastValueFrom(this._http.get(url, httpOptions));
    } catch (error) {
    }
  }

  getAppUserPermissions(idApp: string): Observable<any> {
    const options = { headers: this.headers, withCredentials: true };
    const url = `${this.serviceUrl}/userpermissions?appId=${idApp}`;

    return this._http.get(url, options);
  }

  getUserParty(): Observable<any> {
    const url = this.partyUrl + '/userparty';
    return this._http.get(url, httpOptions)
  }


  getLanguages(): Observable<any> {
    const url = `${this.getServerBaseUrl}/userprofile/languages`;
    return this._http.get(url, httpOptions)
  }

  getTimezones(): Observable<any> {
    const url = `${this.getServerBaseUrl}/userprofile/timezones`;
    return this._http.get(url, httpOptions)
  }


  getContacts(partyId: String): Observable<any> {
    const url = `${this.getServerBaseUrl}/partycontact?partyId=${partyId}`;
    return this._http.get(url, httpOptions)
  }

  getLocations(partyId: String): Observable<any> {
    const url = `${this.getServerBaseUrl}/partyaddress?partyId=${partyId}`;
    return this._http.get(url, httpOptions)
  }

  getCountries(): Observable<any> {
    const url = `${this.serviceGeographyUrl}/countries`;
    return this._http.get(url, httpOptions)
  }

  //#endregion

  //#region User Operations

  public async setMenuIsOpen(isOpen: Boolean): Promise<any> {
    try {
      const url = `${this.suiteUrl}/setMenuOpen`;
      await lastValueFrom(this._http.put(url, { isOpen }, httpOptions));
      return true;
    } catch (error) {
    }
  }

  public isAuthenticated(): Observable<boolean> {
    return this._http.get<boolean>(this.serviceUrl, httpOptions).pipe(
      catchError(e => of(false))
    );
  }

  signOut(): Observable<any> {
    const options = { headers: this.headers, withCredentials: true };
    const url = this.serviceUrl + '/signout';
    return this._http.get(url, options)
  }

  removeImpersonate(): Observable<any> {
    const url = this.getServerBaseUrl + '/suite/removeimpersonateclaim';
    return this._http.put(url, null, httpOptions);
  }

  revokeImpersonationSession(sessionId: string): Observable<void> {
    const url = `${requireHostUrl(this.config, 'authn')}/v1/me/impersonations/${sessionId}`;
    return this._http.delete<void>(url, httpOptions);
  }

  impersonateActive(): Observable<any> {
    const url = this.getServerBaseUrl + '/suite/impersonateactive';
    return this._http.get(url, httpOptions)
  }

  saveAccountData(accountData: any): Observable<any> {
    const url = `${this.getServerBaseUrl}/userprofile`;
    return this._http.put(url, accountData, httpOptions)
  }

  //#endregion

  //#region Owner

  setDataOwner(id: string): Promise<any> {
    const params = { dataOwnerId: id }

    const url = `${this.suiteUrl}/setdataowner`;
    return lastValueFrom(this._http.post(url, params, httpOptions))
  }

  getDataOwner(): Promise<any> {
    const url = this.getServerBaseUrl + '/suite/getdataowner';
    const context = new HttpContext().set(LEGACY_ASP_SESSION_PROBE, true);
    return lastValueFrom(this._http.get(url, { ...httpOptions, context }));
  }

  /**
   * Forza il re-bootstrap della sessione legacy ASP (.AspNet.Cookies).
   * Esegue il redirect verso il monolite legacy /auth/login (stesso flusso di init
   * "login legacy con bootstrapped"). Preserva il deep link corrente.
   * Usato quando la sessione legacy è scaduta mentre quella moderna (Bearer/authToken) è ancora valida.
   *
   * `domain_hint` deve essere il dominio in chiaro (es. `neworg.net`), non il
   * ciphertext del cookie `authTypeDomain`: si passa da `getCookie()` che decifra.
   */
  forceLegacyCookieBootstrap(redirectUrl: string = this._document.location.href): void {
    const lastAttempt = sessionStorage.getItem('cookieBootstrapAttempt');
    if (lastAttempt && Date.now() - Number(lastAttempt) < 10000) {
      return;
    }
    sessionStorage.setItem('cookieBootstrapAttempt', String(Date.now()));

    const backendBase = this.getServerADBaseUrl;

    const returnUrl = new URL(redirectUrl);
    returnUrl.searchParams.set('cookieBootstrapped', 'true');
    const finalRedirectUrl = returnUrl.toString();

    const domainHint = this.getCookie('authTypeDomain') || '';

    let url = `${backendBase}/auth/login?redirectUrl=${encodeURIComponent(finalRedirectUrl)}`;
    if (domainHint) {
      url += `&domain_hint=${encodeURIComponent(domainHint)}`;
    }

    this._document.location.href = url;
  }

  //#endregion

  //#region Alert

  getUserAlerts(): Observable<any> {
    return this._http.get(this.notificationUrl, httpOptions)
  }

  deleteAlert(id: Number): Observable<any> {
    const url = `${this.notificationUrl}?id=${id}`;
    return this._http.delete(url, httpOptions);
  }

  updateAsRead(id: any): Observable<any> {
    const url = `${this.notificationUrl}?id=${id}`;
    return this._http.put(url, httpOptions);
  }

  //#endregion

  //#region Activity

  createActivity(): Observable<any> {
    const url = `${this.activityUrl}/create`;
    return this._http.get(url, httpOptions)
  }

  //#endregion

  //#region Cookie

  /**
* @param {string} key Id to use for lookup.
* @returns {string} Raw cookie value.
*/
  getCookie(key: string) {
    const cookie = this._cookieService.get(key);

    try {
      const decryptedValue = this.decryptValue(cookie);
      return decryptedValue && decryptedValue.trim().length > 0 ? decryptedValue : cookie;

    } catch (e) {
      return cookie;
    }
  }
  //#endregion

  //#region Crypting

  encryptValue(value: string): string {
    const key = CryptoJS.enc.Utf8.parse(this.config.cryptingKey);
    const encrypted = CryptoJS.AES.encrypt(value, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return encrypted.toString();
  }


  decryptValue(value: string): string {
    const key = CryptoJS.enc.Utf8.parse(this.config.cryptingKey);
    const decrypted = CryptoJS.AES.decrypt(value, key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  //#endregion

  public removeConfirm(text): void {
    this.remove$.next(text);
  }
}
